import SessionService from '@/modules/core/session/services/SessionService';
import { commits, dispatches, getters } from '@/modules/core/app/helpers/store';
import ThemeUIService from '@/modules/core/app/services/ThemeUIService';
import SegmentService from '@/modules/core/app/services/SegmentService';
import { Env } from '@/env';
import BrandMappingService from '@/modules/core/session/services/BrandMappingService';
import CacheHelper from '@/modules/core/app/helpers/CacheHelper';
import { CurrentThemeTypeCacheKey } from '@/modules/ta/preference/preference.constants';
import {
  ThemeType,
  HIPAA_ACK_REQUIRED,
  TWO_STEP_VERIFICATION_REQUIRED,
} from '@/modules/core/app/constants/app.constants';
import { SessionResponseType, SessionVars } from '@/modules/core/session/session.constants';
import { CustomError } from '@/modules/core/app/models/CustomError';
import NotificationService from '@/modules/core/ui/services/NotificationService';
import { resetI18n } from '@/i18n';
import DocumentUIService from '@/modules/core/app/services/DocumentUIService';
import { MCPCHAT_STORAGE_KEY } from '@/modules/core/mcpchat/constants/mcpchat.constants';
import { clearAllCachedConversationMessages } from '@/modules/core/mcpchat/store/mcpchat.cache';
import ReportStudioElementClipboardService from '@/modules/ta/reportstudio/services/ReportStudioElementClipboardService';
import ReportStudioPageClipboardService from '@/modules/ta/reportstudio/services/ReportStudioPageClipboardService';

export const actions = {
  async login(
    {},
    {
      email,
      password,
      recaptcha,
      hipaa_checked,
      two_step_verification_code,
      resend_verification_code,
      remember_this_device,
    }
  ) {
    commits.session.isLoggingIn(true);
    try {
      const response = await SessionService.login(
        email,
        password,
        recaptcha,
        hipaa_checked,
        two_step_verification_code,
        resend_verification_code,
        remember_this_device
      );
      if (response.status === 'success') {
        commits.session.setUserSettings(response.data.settings);
        if (getters.session.getUserSettings()) {
          await dispatches.session.updateUserSettings(getters.session.getUserSettings());
        }
      } else if (response.status === SessionResponseType.EXPIRED_PASSWORD) {
        throw new CustomError(
          i18n.$t('Password Expired. Please reset your password'),
          SessionResponseType.EXPIRED_PASSWORD
        );
      } else if (response.status === TWO_STEP_VERIFICATION_REQUIRED) {
        if (response.message) {
          NotificationService.show(response.message);
        }
        const settings = getters.session.getUserSettings();
        settings.isTwoStepVerificationEnabledForUser = true;
        if (response.use_recaptcha) {
          settings.useTwoStepRecaptcha = true;
        }
        commits.session.setUserSettings(settings);
      } else {
        NotificationService.showPersistent(response.data);
        if (response.status === HIPAA_ACK_REQUIRED) {
          const settings = getters.session.getUserSettings();
          settings.needsHipaaAcknowledgement = true;
          commits.session.setUserSettings(settings);
        }
      }
    } catch (e) {
      if (e.message.use_recaptcha) {
        NotificationService.showPersistent(e.message.message);
        throw e;
      } else if (e instanceof CustomError) {
        throw e;
      }
      NotificationService.showPersistent(e.message);
    } finally {
      commits.session.isLoggingIn(false);
      window.localStorage.removeItem(SessionVars.PERSIST_ADVANCE_FILTERS);
    }
  },
  async updateUserSettings({}, currentSettings = null) {
    try {
      if (!currentSettings) {
        const themeType = CacheHelper.get(CurrentThemeTypeCacheKey);
        if (themeType && themeType !== ThemeType.LIGHT) {
          ThemeUIService.updateTheme(null, themeType);
        }
      }
      const { settings } = currentSettings
        ? { settings: currentSettings }
        : await SessionService.getUserSettings();

      const themeType = Env.isProduction()
        ? settings.themeType
        : ThemeUIService.getUserTheme(settings.user?.id) ?? settings.themeType;

      // TODO: @dannyyassine to be removed when merged to master.
      // This simulates when frontend html is delivered to the browser
      //  with already set CSS vars in index.html
      if (settings.currentThemeColor) {
        ThemeUIService.updateTheme(settings.currentThemeColor, themeType);
        CacheHelper.set(CurrentThemeTypeCacheKey, themeType);
      }
      commits.session.setUserSettings(settings);

      // Set the locale based on the latest user settings
      resetI18n(settings.locale).then(() => {
        DocumentUIService.initCssTranslations();
      });

      if (!Env.isUnitTesting() && getters.session.isLoggedIn() && !window.isExport) {
        // Defer Segment/Pendo init until main thread is idle. Pendo loads 16+
        // guide scripts that block ~1.6s of main thread; this frees the boot
        // path. Analytics calls use optional chaining so pre-init events no-op.
        if (typeof requestIdleCallback === 'function') {
          requestIdleCallback(() => SegmentService.initialize(settings), { timeout: 10000 });
        } else {
          setTimeout(() => SegmentService.initialize(settings), 5000);
        }
      }
    } catch (e) {
      NotificationService.showPersistent(e.message);
    }
  },

  async impersonateUser({}, userId) {
    try {
      dispatches.list.clearListParamsState();
      const impersonatedUser = await SessionService.impersonateUser(userId);
      commits.session.setUserSettings(impersonatedUser);
    } catch (e) {
      NotificationService.showPersistent(e.message);
    }
  },

  async logout() {
    dispatches.list.clearListParamsState();
    commits.session.isLoggingOut(true);
    commits.session.resetLoginRedirectPath();
    try {
      commits.session.setLoggedOut();
      await SessionService.logout();
    } catch (e) {
      NotificationService.showPersistent(e.message);
    } finally {
      commits.session.isLoggingOut(false);
      window.localStorage.removeItem(SessionVars.PERSIST_ADVANCE_FILTERS);
      try {
        window.sessionStorage.removeItem(MCPCHAT_STORAGE_KEY);
      } catch (error) {
        // Ignore storage failures.
      }
      clearAllCachedConversationMessages();
      ReportStudioElementClipboardService.clear();
      ReportStudioPageClipboardService.clear();
    }
  },

  setLoginRedirectPath(state, path) {
    commits.session.setLoginRedirectPath(path);
  },

  resetLoginRedirectPath() {
    commits.session.resetLoginRedirectPath();
  },

  setShowWelcomeModal({}, value) {
    commits.session.setShowWelcomeModal(value);
  },
  /**
   * @param {SessionState} state
   * @param mapping
   * @returns {Promise<void>}
   */
  async setBrandMapping({ state }, mapping) {
    if (state.brandMapping) {
      return;
    }
    commits.session.setBrandMapping(mapping ?? (await BrandMappingService.getMapping()));
  },
};
